import React, { useState } from 'react';
import { NotificationsActive, NotificationsNone, Delete, MailOutline } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { toggleListNotification, useNotificationSubscriptions, useCategories, useAIPortfolios, useUpdateAIPortfolio, useSignalWatches, removeSignalWatch, useWatchBacktests, useSignals, useWebhookConfig, saveWebhookConfig, deleteWebhookConfig, testWebhook, useCalendarFeed, regenerateCalendarFeed, useSignalsFeed, regenerateSignalsFeed, useCallEmailPreference, setCallEmailPreference } from '../services/api';
import { STRATEGY_LABELS } from '../constants/signalStrategies';
import { usePushNotifications } from '../hooks/usePushNotifications';

const TABS = [
  { id: 'lists', label: 'Lists' },
  { id: 'portfolios', label: 'Portfolios' },
  { id: 'signals', label: 'Signals' },
  { id: 'calendar', label: 'Calendar' },
];

const POSITION_COLORS = { long: '#16a34a', short: '#dc2626', flat: '#9ca3af' };

const fmtStatPct = (value) => (
  <span style={{ color: value >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
    {value >= 0 ? '+' : '−'}{Math.abs(value).toFixed(Math.abs(value) >= 10 ? 0 : 1)}%
  </span>
);

// Aggregate the watch's REAL recorded round trips (signal_trades via the
// signals API) — only trades matching the watch's current shape/period, so a
// re-fitted watch doesn't inherit an old fit's record.
const liveStatsFor = (closedTrades, w) => {
  const own = closedTrades.filter((t) => t.ticker === w.ticker && t.strategy === w.strategy && t.period === w.period && t.pl_pct != null);
  if (!own.length) return null;
  return {
    trades: own.length,
    wins: own.filter((t) => t.pl_pct > 0).length,
    totalPl: own.reduce((sum, t) => sum + t.pl_pct, 0),
  };
};

// Per-watch performance: "live" = actual signaled trades since the watch was
// enabled (ground truth, shown first); "fit" = backtest of the saved
// shape/period over that stock's own saved chart range, so it matches the
// Trading Range chart. New watches have no live trades yet — fit is the
// context that justifies keeping them.
const WatchStats = ({ fit, live }) => {
  if (!live && !(fit?.trades > 0)) return null;
  return (
    <span style={{ fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap', display: 'inline-flex', gap: '8px' }}>
      {live && (
        <span title="Actual trades this watch signaled since it was enabled: cumulative P&L · won/total">
          live {fmtStatPct(live.totalPl)} · {live.wins}/{live.trades}
        </span>
      )}
      {fit?.trades > 0 && (
        <span title={`Backtest of this fit over the ${fit.rangeLabel} range selected on this stock's Trading Range chart`}>
          fit {fmtStatPct(fit.totalReturn * 100)} · {fit.wins}/{fit.trades} · {fit.rangeLabel}
        </span>
      )}
    </span>
  );
};

// Shared compact styles — the modal is a settings panel, keep it dense
const STYLES = {
  sectionHeader: { fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' },
  listRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', background: '#f9fafb', borderRadius: '6px' },
  emptyBox: { padding: '10px 12px', background: '#f9fafb', borderRadius: '6px' },
  smallBtn: { fontSize: '12px', padding: '4px 10px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', textDecoration: 'none', display: 'inline-block' },
  urlBox: { fontSize: '12px', color: '#111827', wordBreak: 'break-all', background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: '6px', padding: '6px 8px', userSelect: 'all' },
  finePrint: { fontSize: '11px', color: '#9ca3af', margin: 0, lineHeight: 1.45 },
};

// Copy / regenerate actions shared by the token-in-URL feeds (calendar ICS, signals JSON)
const useFeedActions = (feedUrl, queryKey, rotateFn, rotateOkText) => {
  const queryClient = useQueryClient();
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const copy = async () => {
    if (!feedUrl) return;
    try {
      await navigator.clipboard.writeText(feedUrl);
      setMsg({ ok: true, text: 'Copied' });
    } catch {
      setMsg({ ok: false, text: 'Copy failed — select the URL and copy manually' });
    }
  };

  const regenerate = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await rotateFn();
      queryClient.invalidateQueries([queryKey]);
      setMsg({ ok: true, text: rotateOkText });
    } catch (error) {
      setMsg({ ok: false, text: error.message || 'Failed to regenerate' });
    } finally {
      setBusy(false);
    }
  };

  return { msg, busy, copy, regenerate };
};

const FeedMessage = ({ msg }) => msg
  ? <p style={{ fontSize: '11px', color: msg.ok ? '#16a34a' : '#dc2626', margin: 0 }}>{msg.text}</p>
  : null;

/**
 * Account-level opt-in for the earnings-call briefing email (issue #207).
 *
 * Sits beside the push toggle rather than inside a tab because, like push, it is a DELIVERY
 * channel and not a per-list subscription: the recipient set is derived from the watchlist the
 * user already maintains, so there is nothing here to subscribe to individually.
 *
 * Backed by its OWN column via `/earnings-highlights/preferences`, never by `users.preferences`.
 * That column is written as a whole object by two independent writers (fp_user replaces it
 * outright; App.js posts `{...userSettings, key}` from a login-time localStorage snapshot), so a
 * flag stored there would have been silently wiped the next time the user changed a filter — the
 * briefings would simply stop, with nothing logged. This component therefore performs no
 * read-modify-write of shared state at all.
 *
 * Hidden for non-power users: the analysis is Pro-gated in the worker, so offering the switch to
 * everyone would be an opt-in that delivers nothing.
 */
const EarningsCallEmailRow = ({ userEmail }) => {
  const queryClient = useQueryClient();
  const { data: pref, isLoading, isError } = useCallEmailPreference(userEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // A failed fetch must not render as "off" — the hook throws rather than defaulting, and the row
  // stays hidden, so the switch is never shown in a position it cannot vouch for.
  if (isLoading || isError || !pref?.isPowerUser) return null;

  const enabled = pref.earningsCallEmail === true;

  const handleToggle = async () => {
    setBusy(true);
    setError(null);
    try {
      await setCallEmailPreference(!enabled);
      await queryClient.invalidateQueries({ queryKey: ['callEmailPreference', userEmail] });
    } catch (e) {
      setError(e.message || 'Could not save that — try again');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginBottom: '10px', padding: '8px 10px', background: '#f7f5ff', border: '1px solid #e6e0ff', borderRadius: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <MailOutline style={{ fontSize: 18, color: enabled ? '#6d28d9' : '#6b7280' }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>Earnings call briefings (email)</div>
            <div style={{ fontSize: '11px', color: '#6b7280' }}>
              {enabled
                ? 'A written briefing whenever a watchlist stock holds an earnings call'
                : 'Get a written briefing by email as soon as a watchlist stock\u2019s call is transcribed'}
            </div>
          </div>
        </div>
        <button
          onClick={handleToggle}
          disabled={busy}
          style={{
            fontSize: '12px', padding: '4px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer',
            background: enabled ? '#e5e7eb' : '#6d28d9',
            color: enabled ? '#374151' : '#fff',
            opacity: busy ? 0.6 : 1,
            flexShrink: 0,
          }}
        >
          {busy ? '...' : enabled ? 'Disable' : 'Enable'}
        </button>
      </div>
      {error && <div style={{ fontSize: '11px', color: '#b91c1c', marginTop: '6px' }}>{error}</div>}
    </div>
  );
};

// Device-level web push opt-in. Sits above the tabs because push delivery
// spans all notification types (signals, watchlist earnings, ...).
const PushToggleRow = ({ userEmail }) => {
  const { supported, permission, subscribed, busy, error, enable, disable, sendTest } = usePushNotifications(userEmail);
  const [testMsg, setTestMsg] = useState(null);

  if (!supported) return null;

  const handleToggle = async () => {
    setTestMsg(null);
    if (subscribed) await disable(); else await enable();
  };

  const handleTest = async () => {
    setTestMsg(null);
    const result = await sendTest();
    if (result) {
      setTestMsg(result.sent > 0
        ? { ok: true, text: `Sent to ${result.sent} device${result.sent === 1 ? '' : 's'}` }
        : { ok: false, text: 'No active devices received it' });
    }
  };

  return (
    <div style={{ marginBottom: '10px', padding: '8px 10px', background: '#f0f7ff', border: '1px solid #dbeafe', borderRadius: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          {subscribed ? <NotificationsActive style={{ fontSize: 18, color: '#2563eb' }} /> : <NotificationsNone style={{ fontSize: 18, color: '#6b7280' }} />}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>Browser notifications</div>
            <div style={{ fontSize: '11px', color: '#6b7280' }}>
              {subscribed ? 'Enabled on this device' : 'Get signal & earnings alerts on this device, even when the app is closed'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {subscribed && (
            <button
              onClick={handleTest}
              disabled={busy}
              style={{ ...STYLES.smallBtn, opacity: busy ? 0.6 : 1 }}
            >
              Send test
            </button>
          )}
          <button
            onClick={handleToggle}
            disabled={busy || permission === 'denied'}
            style={{
              fontSize: '12px', padding: '4px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer',
              background: subscribed ? '#e5e7eb' : '#2563eb',
              color: subscribed ? '#374151' : '#fff',
              opacity: busy || permission === 'denied' ? 0.6 : 1,
            }}
          >
            {busy ? '...' : subscribed ? 'Disable' : 'Enable'}
          </button>
        </div>
      </div>
      {permission === 'denied' && !subscribed && (
        <div style={{ fontSize: '11px', color: '#b91c1c', marginTop: '6px' }}>
          Notifications are blocked for this site — allow them in your browser's site settings first.
        </div>
      )}
      {error && permission !== 'denied' && (
        <div style={{ fontSize: '11px', color: '#b91c1c', marginTop: '6px' }}>{error}</div>
      )}
      {testMsg && (
        <div style={{ fontSize: '11px', color: testMsg.ok ? '#15803d' : '#b91c1c', marginTop: '6px' }}>{testMsg.text}</div>
      )}
    </div>
  );
};

const NotificationsModal = ({ userEmail, userLists = [] }) => {
  const queryClient = useQueryClient();
  const { data: subscribedListIds = [], isLoading: isLoadingSubscriptions } = useNotificationSubscriptions(userEmail);
  const { data: categories = [], isLoading: isLoadingCategories } = useCategories();
  const { data: portfolios = [], isLoading: isLoadingPortfolios } = useAIPortfolios(userEmail);
  const updatePortfolio = useUpdateAIPortfolio();
  const { data: watchData } = useSignalWatches(!!userEmail, userEmail);
  const { data: webhook } = useWebhookConfig(!!userEmail, userEmail);
  const [activeTab, setActiveTab] = useState('lists');
  const [unsubscribingId, setUnsubscribingId] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookBusy, setWebhookBusy] = useState(false);
  const [webhookMsg, setWebhookMsg] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  // Only fetch when the tab is opened — the GET mints the user's feed token on first use
  const { data: calendarFeed, isLoading: isLoadingCalendarFeed } = useCalendarFeed(activeTab === 'calendar' && !!userEmail, userEmail);
  const calendarActions = useFeedActions(calendarFeed?.url, 'calendarFeed', regenerateCalendarFeed,
    'New link generated — the old one no longer works. Re-subscribe in your calendar app.');
  // The signals feed URL is a read credential (anyone with it sees your positions),
  // so unlike the calendar tab the token is only minted on an explicit reveal click.
  const [signalsFeedRevealed, setSignalsFeedRevealed] = useState(false);
  const { data: signalsFeed, isLoading: isLoadingSignalsFeed } = useSignalsFeed(signalsFeedRevealed && !!userEmail, userEmail);
  const signalsActions = useFeedActions(signalsFeed?.url, 'signalsFeed', regenerateSignalsFeed,
    'New URL generated — the old one no longer works. Update any apps or scripts using it.');

  const signalWatches = watchData?.watches || [];
  // Stats fetches only fire while the Signals tab is open: one batched price
  // request for the fit backtests, one signals call (max window) for the
  // watches' real recorded trades.
  const { data: watchBacktests } = useWatchBacktests(signalWatches, activeTab === 'signals', watchData?.strategies);
  const { data: signalsData } = useSignals(activeTab === 'signals' && !!userEmail, userEmail, 180);
  const closedTrades = signalsData?.closed_trades || [];

  const handleRemoveWatch = async (ticker) => {
    setUnsubscribingId(ticker);
    try {
      await removeSignalWatch(ticker);
      queryClient.invalidateQueries(['signalWatches']);
    } catch (error) {
      console.error('[NotificationsModal] Failed to remove watch:', error);
    } finally {
      setUnsubscribingId(null);
    }
  };

  const handleSaveWebhook = async () => {
    setWebhookBusy(true);
    setWebhookMsg(null);
    try {
      await saveWebhookConfig(webhookUrl.trim());
      setWebhookUrl('');
      queryClient.invalidateQueries(['webhookConfig']);
      setWebhookMsg({ ok: true, text: 'Saved' });
    } catch (error) {
      setWebhookMsg({ ok: false, text: error.message || 'Failed to save' });
    } finally {
      setWebhookBusy(false);
    }
  };

  const handleTestWebhook = async () => {
    setWebhookBusy(true);
    setWebhookMsg(null);
    try {
      const result = await testWebhook();
      setWebhookMsg(result.success ? { ok: true, text: `Delivered (HTTP ${result.status})` } : { ok: false, text: `Failed: ${result.error || result.status}` });
    } finally {
      setWebhookBusy(false);
    }
  };

  const handleDeleteWebhook = async () => {
    setWebhookBusy(true);
    setWebhookMsg(null);
    try {
      await deleteWebhookConfig();
      queryClient.invalidateQueries(['webhookConfig']);
      setWebhookMsg(null);
    } finally {
      setWebhookBusy(false);
    }
  };

  const SPECIAL_SUBSCRIPTIONS = {
    market_overview: 'Daily Market Overview'
  };

  const getListName = React.useCallback((listId) => {
    if (SPECIAL_SUBSCRIPTIONS[listId]) return SPECIAL_SUBSCRIPTIONS[listId];
    const userList = userLists.find(list => list.id === listId);
    if (userList) return userList.name || 'Unnamed List';
    for (const category of categories) {
      const filter = category.filters?.find(f => f.id === listId);
      if (filter) return filter.name;
    }
    return listId;
  }, [categories, userLists]);

  const handleUnsubscribeList = async (listId) => {
    setUnsubscribingId(listId);
    try {
      await toggleListNotification(listId, false);
      queryClient.invalidateQueries(['notificationSubscriptions', userEmail]);
    } catch (error) {
      console.error('[NotificationsModal] Failed to unsubscribe:', error);
    } finally {
      setUnsubscribingId(null);
    }
  };

  const handleTogglePortfolioNotification = async (portfolio) => {
    const newValue = portfolio.notifications_enabled === 0 ? 1 : 0;
    await updatePortfolio.mutateAsync({ id: portfolio.id, notifications_enabled: newValue });
    queryClient.invalidateQueries(['aiPortfolios', userEmail]);
  };

  const isLoading = isLoadingSubscriptions || isLoadingCategories || isLoadingPortfolios;
  const portfoliosWithNotifications = portfolios.filter?.(p => p.status !== 'deleted') || [];

  const deliveryNote = (text, palette = { bg: '#f9fafb', border: '#f3f4f6' }) => (
    <div style={{ fontSize: '12px', color: '#6b7280', background: palette.bg, border: `1px solid ${palette.border}`, borderRadius: '6px', padding: '6px 10px', lineHeight: 1.45 }}>
      {text}
    </div>
  );

  const renderListsTab = () => {
    const note = deliveryNote(<>Delivered by <strong>email</strong> to your account whenever a list you follow changes its holdings — checked twice each weekday, around market open and close.</>);
    if (subscribedListIds.length === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {note}
          <div style={STYLES.emptyBox}>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>No list subscriptions yet.</p>
            <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 0' }}>Click the bell icon on any smart list to receive alerts when it updates.</p>
          </div>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {note}
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {subscribedListIds.map((listId) => (
          <li key={listId} style={STYLES.listRow}>
            <span style={{ fontSize: '13px', color: '#111827' }}>{getListName(listId)}</span>
            <button
              onClick={() => handleUnsubscribeList(listId)}
              disabled={unsubscribingId === listId}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9ca3af', opacity: unsubscribingId === listId ? 0.5 : 1 }}
              title="Unsubscribe"
            >
              {unsubscribingId === listId ? <span style={{ fontSize: '12px' }}>...</span> : <Delete style={{ fontSize: 16 }} />}
            </button>
          </li>
        ))}
        </ul>
      </div>
    );
  };

  const renderPortfoliosTab = () => {
    const note = deliveryNote(<>Delivered by <strong>email</strong> to your account each time an AI portfolio rebalances and places trades.</>);
    if (portfoliosWithNotifications.length === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {note}
          <div style={STYLES.emptyBox}>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>No AI portfolios yet.</p>
            <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 0' }}>Create an AI portfolio to receive trade notifications on rebalance.</p>
          </div>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {note}
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {portfoliosWithNotifications.map((portfolio) => {
          const isEnabled = portfolio.notifications_enabled !== 0;
          return (
            <li key={portfolio.id} style={STYLES.listRow}>
              <div>
                <span style={{ fontSize: '13px', color: '#111827' }}>{portfolio.name}</span>
                <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '8px' }}>{portfolio.strategy}</span>
              </div>
              <button
                onClick={() => handleTogglePortfolioNotification(portfolio)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: isEnabled ? '#8b5cf6' : '#9ca3af' }}
                title={isEnabled ? 'Notifications on (click to disable)' : 'Notifications off (click to enable)'}
              >
                {isEnabled
                  ? <NotificationsActive style={{ fontSize: 18 }} />
                  : <NotificationsNone style={{ fontSize: 18 }} />}
              </button>
            </li>
          );
        })}
        </ul>
      </div>
    );
  };

  const renderSignalsFeedSection = () => (
    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '10px' }}>
      <div style={STYLES.sectionHeader}>Signals API (pull)</div>
      <p style={{ ...STYLES.finePrint, margin: '0 0 6px' }}>
        A read-only <strong>JSON feed</strong> of your recent signal events and open positions — same fields as the webhook digest. Poll it from trading scripts, sheets, or other apps instead of (or alongside) the webhook.
      </p>
      {!signalsFeedRevealed ? (
        <button onClick={() => setSignalsFeedRevealed(true)} style={STYLES.smallBtn}>Show feed URL</button>
      ) : isLoadingSignalsFeed || !signalsFeed?.url ? (
        <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Loading your feed URL...</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={STYLES.urlBox}>{signalsFeed.url}</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={signalsActions.copy} style={{ ...STYLES.smallBtn, background: '#8b5cf6', color: 'white', border: 'none' }}>Copy link</button>
            <button onClick={signalsActions.regenerate} disabled={signalsActions.busy} style={{ ...STYLES.smallBtn, borderColor: '#fca5a5', color: '#dc2626', opacity: signalsActions.busy ? 0.5 : 1 }}>Regenerate URL</button>
          </div>
          <p style={STYLES.finePrint}>
            Anyone with this URL can read your signals and positions — regenerate it if it leaks. Append <code>?days=N</code> (up to 180) to control the event lookback.
          </p>
          <FeedMessage msg={signalsActions.msg} />
        </div>
      )}
    </div>
  );

  const renderSignalsTab = () => {
    const configured = webhook?.configured;
    const hasWatchesNoDelivery = signalWatches.length > 0 && !configured;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* How delivery works */}
        {deliveryNote(
          <>Watched stocks are checked <strong>every trading morning, before the US open</strong> (using the prior session's close). When one triggers its strategy's entry or exit, the alert is sent to your <strong>webhook</strong> in real time. Email delivery is coming soon.</>,
          { bg: '#f5f3ff', border: '#ede9fe' }
        )}

        {/* Delivery-not-configured warning */}
        {hasWatchesNoDelivery && (
          <div style={{ fontSize: '12px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '6px 10px' }}>
            ⚠️ You're watching {signalWatches.length} stock{signalWatches.length === 1 ? '' : 's'} but haven't set up delivery yet — add a webhook below to actually receive alerts.
          </div>
        )}

        {/* Watched stocks */}
        <div>
          <div style={STYLES.sectionHeader}>Watched stocks</div>
          {signalWatches.length === 0 ? (
            <div style={STYLES.emptyBox}>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>No signal watches yet.</p>
              <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 0' }}>Open a stock's Trading Range chart, pick a strategy/period, and click <strong>🔔 Watch</strong> to get alerted when it triggers.</p>
            </div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {signalWatches.map((w) => (
                <li key={w.ticker} style={STYLES.listRow}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{w.ticker}</span>
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>{STRATEGY_LABELS[w.strategy] || w.strategy} · {w.period}D</span>
                    <WatchStats fit={watchBacktests?.[w.ticker]} live={liveStatsFor(closedTrades, w)} />
                    <span style={{ fontSize: '10px', fontWeight: 600, color: POSITION_COLORS[w.position] || '#9ca3af', textTransform: 'uppercase' }}>{w.position}</span>
                  </div>
                  <button
                    onClick={() => handleRemoveWatch(w.ticker)}
                    disabled={unsubscribingId === w.ticker}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9ca3af', opacity: unsubscribingId === w.ticker ? 0.5 : 1 }}
                    title="Stop watching"
                  >
                    {unsubscribingId === w.ticker ? <span style={{ fontSize: '12px' }}>...</span> : <Delete style={{ fontSize: 16 }} />}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {watchData?.max != null && (
            <p style={{ fontSize: '11px', color: '#9ca3af', margin: '4px 0 0' }}>{signalWatches.length}/{watchData.max} watches used</p>
          )}
        </div>

        {/* Webhook */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '10px' }}>
          <div style={STYLES.sectionHeader}>Webhook (real-time delivery)</div>
          <p style={{ ...STYLES.finePrint, margin: '0 0 6px' }}>
            Signals POST to your https endpoint the moment they fire. <strong>Discord</strong> and <strong>Slack</strong> URLs receive a formatted message; any other endpoint gets <strong>structured JSON</strong> signed with <code>X-QuantApe-Signature</code> — ready for a trading or automation script to verify and act on.
          </p>
          {configured ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ fontSize: '12px', color: '#111827', wordBreak: 'break-all' }}>{webhook.url}</div>
              <div style={{ fontSize: '11px', color: webhook.enabled ? '#16a34a' : '#dc2626' }}>
                {webhook.enabled ? '● Enabled' : '● Disabled'}
                {webhook.failure_count > 0 && ` · ${webhook.failure_count} recent failure(s)`}
                {webhook.last_error && ` · ${webhook.last_error}`}
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                Signing secret:{' '}
                {showSecret
                  ? <code style={{ userSelect: 'all' }}>{webhook.secret}</code>
                  : <button onClick={() => setShowSecret(true)} style={{ background: 'none', border: 'none', color: '#8b5cf6', cursor: 'pointer', fontSize: '11px', textDecoration: 'underline', padding: 0 }}>reveal</button>}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={handleTestWebhook} disabled={webhookBusy} style={STYLES.smallBtn}>Send test</button>
                <button onClick={handleDeleteWebhook} disabled={webhookBusy} style={{ ...STYLES.smallBtn, borderColor: '#fca5a5', color: '#dc2626' }}>Remove</button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '6px' }}>
              <input
                type="url"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://..."
                style={{ flex: 1, fontSize: '12px', padding: '6px 8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              />
              <button onClick={handleSaveWebhook} disabled={webhookBusy || !webhookUrl.trim()} style={{ fontSize: '12px', padding: '4px 12px', borderRadius: '6px', border: 'none', background: '#8b5cf6', color: 'white', cursor: 'pointer', opacity: webhookBusy || !webhookUrl.trim() ? 0.5 : 1 }}>Save</button>
            </div>
          )}
          {webhookMsg && <p style={{ fontSize: '11px', color: webhookMsg.ok ? '#16a34a' : '#dc2626', margin: '6px 0 0' }}>{webhookMsg.text}</p>}
        </div>

        {/* Pull API — the external feed consumed by other apps */}
        {renderSignalsFeedSection()}
      </div>
    );
  };

  const renderCalendarTab = () => {
    const feedUrl = calendarFeed?.url;
    const webcalUrl = feedUrl ? feedUrl.replace(/^https:/, 'webcal:') : null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {deliveryNote(<>Subscribe to your personal feed and your calendar app shows an <strong>event for each market bellwether's upcoming earnings</strong> (mega caps ≥ $100B whose reports move the market) — timed <strong>before open / after close</strong> when the date is confirmed, all-day when still estimated — plus <strong>high-impact US economic events</strong> (CPI, FOMC, jobs) at their release times. It updates automatically as dates change — calendar apps typically refresh subscribed feeds every few hours.</>)}
        {isLoadingCalendarFeed || !feedUrl ? (
          <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>Loading your feed link...</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={STYLES.urlBox}>{feedUrl}</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={calendarActions.copy} style={{ ...STYLES.smallBtn, background: '#8b5cf6', color: 'white', border: 'none' }}>Copy link</button>
              <a href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}`} target="_blank" rel="noopener noreferrer" style={STYLES.smallBtn}>Add to Google Calendar</a>
              <a href={webcalUrl} style={STYLES.smallBtn}>Add to Apple Calendar</a>
              <button onClick={calendarActions.regenerate} disabled={calendarActions.busy} style={{ ...STYLES.smallBtn, borderColor: '#fca5a5', color: '#dc2626', opacity: calendarActions.busy ? 0.5 : 1 }}>Regenerate link</button>
            </div>
            <p style={STYLES.finePrint}>
              In other apps (Outlook etc.), add it as a calendar subscription "from URL". Dates marked <strong>(est.)</strong> are estimates not yet confirmed by the company. The link is personal to your account — regenerate it if it leaks.
            </p>
          </div>
        )}
        <FeedMessage msg={calendarActions.msg} />
      </div>
    );
  };

  if (isLoading) {
    return <div className="text-gray-500 text-sm p-4">Loading...</div>;
  }

  return (
    <div>
      {/* Browser push opt-in — device-level, applies across all notification types */}
      <PushToggleRow userEmail={userEmail} />

      {/* Earnings-call briefing email — account-level, Pro only (issue #207) */}
      <EarningsCallEmailRow userEmail={userEmail} />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #e5e7eb', marginBottom: '10px' }}>
        {TABS.map(({ id, label }) => {
          const count = id === 'lists' ? subscribedListIds.length
            : id === 'signals' ? signalWatches.length
            : id === 'portfolios' ? portfoliosWithNotifications.filter(p => p.notifications_enabled !== 0).length
            : 0;
          return (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                padding: '6px 14px',
                fontSize: '13px',
                fontWeight: activeTab === id ? 600 : 400,
                color: activeTab === id ? '#1d4ed8' : '#6b7280',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === id ? '2px solid #3b82f6' : '2px solid transparent',
                cursor: 'pointer',
                marginBottom: '-1px',
              }}
            >
              {label}{count > 0 ? ` (${count})` : ''}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'lists' ? renderListsTab() : activeTab === 'signals' ? renderSignalsTab() : activeTab === 'calendar' ? renderCalendarTab() : renderPortfoliosTab()}
    </div>
  );
};

export default NotificationsModal;
